import jsPDF from 'jspdf';
import type { AddressInfo } from '../types/blockchain.types';

export function generateAddressPDF(addressInfo: AddressInfo): void {
  const doc = new jsPDF();

  // Constants
  const margin = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  let yPos = margin;

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('BLOCKCHAIN ADDRESS LOOKUP', centerX, yPos, { align: 'center' });
  yPos += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Constancia de Consulta de Direcci\u00f3n Blockchain', centerX, yPos, { align: 'center' });
  yPos += 15;

  // Timestamp
  const now = new Date();
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generado: ${now.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })} (UTC-3)`, centerX, yPos, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  yPos += 15;

  // Section: Network Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Red Blockchain', margin, yPos);
  yPos += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${addressInfo.network.name}`, margin + 5, yPos);
  yPos += 6;
  doc.text(`Chain ID: ${addressInfo.network.chain_id}`, margin + 5, yPos);
  yPos += 6;
  doc.text(`Moneda Nativa: ${addressInfo.network.native_currency}`, margin + 5, yPos);
  yPos += 6;
  doc.text(`Explorador: ${addressInfo.network.explorer_name}`, margin + 5, yPos);
  yPos += 12;

  // Section: Address
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Direcci\u00f3n Consultada', margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  // Split address into chunks for better readability
  const addressChunks = addressInfo.address.match(/.{1,42}/g) || [addressInfo.address];
  addressChunks.forEach(chunk => {
    doc.text(chunk, margin + 5, yPos);
    yPos += 5;
  });
  yPos += 7;

  // Section: Balance
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Balance', margin, yPos);
  yPos += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const balanceInEth = parseFloat(addressInfo.balance);
  doc.text(`${balanceInEth.toFixed(6)} ${addressInfo.network.native_currency}`, margin + 5, yPos);
  yPos += 6;

  // Show wei value for transparency
  const balanceWei = (balanceInEth * 1e18).toLocaleString('fullwide', { useGrouping: false });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`(${balanceWei} wei)`, margin + 5, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 12;

  // Section: Transaction Activity
  if (addressInfo.tx_count !== undefined) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Actividad de Transacciones', margin, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total de Transacciones: ${addressInfo.tx_count}`, margin + 5, yPos);
    yPos += 6;

    if (addressInfo.first_tx_timestamp) {
      const firstTx = new Date(addressInfo.first_tx_timestamp * 1000);
      doc.text(`Primera Transacci\u00f3n: ${firstTx.toLocaleString('es-AR')}`, margin + 5, yPos);
      yPos += 6;
    }

    if (addressInfo.last_tx_timestamp) {
      const lastTx = new Date(addressInfo.last_tx_timestamp * 1000);
      doc.text(`\u00daltima Transacci\u00f3n: ${lastTx.toLocaleString('es-AR')}`, margin + 5, yPos);
      yPos += 6;
    }
    yPos += 8;
  }

  // Section: Explorer Link
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Verificaci\u00f3n Independiente', margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Esta informaci\u00f3n puede ser verificada de forma independiente en:', margin + 5, yPos);
  yPos += 6;

  const explorerUrl = `${addressInfo.network.explorer_url}/address/${addressInfo.address}`;
  doc.setTextColor(0, 0, 255);
  doc.textWithLink(explorerUrl, margin + 5, yPos, { url: explorerUrl });
  doc.setTextColor(0, 0, 0);
  yPos += 15;

  // Footer / Legal Notice
  yPos = doc.internal.pageSize.getHeight() - 40;
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.setFont('helvetica', 'italic');

  const legalText = 'Esta constancia es un registro de la consulta realizada en la blockchain p\u00fablica. '
    + 'Los datos son obtenidos directamente de la red blockchain correspondiente a trav\u00e9s de '
    + 'exploradores de bloques certificados. SignWiz no almacena esta informaci\u00f3n y act\u00faa '
    + '\u00fanicamente como facilitador de la consulta.';

  const splitLegal = doc.splitTextToSize(legalText, pageWidth - 2 * margin);
  doc.text(splitLegal, margin, yPos);
  yPos += splitLegal.length * 4 + 5;

  doc.setFont('helvetica', 'normal');
  doc.text('Powered by SignWiz - Blockchain Legal Tools', centerX, yPos, { align: 'center' });
  yPos += 5;

  // Arteclaw branding (centered)
  doc.setFontSize(9);
  const desarrolladoText = 'Desarrollado por ';
  const arteclawText = 'Arteclaw';
  const textWidth = doc.getTextWidth(desarrolladoText) + doc.getTextWidth(arteclawText);
  const startX = centerX - (textWidth / 2);

  doc.setTextColor(0, 0, 0);
  doc.text(desarrolladoText, startX, yPos);
  doc.setTextColor(0, 0, 255);
  doc.textWithLink(arteclawText, startX + doc.getTextWidth(desarrolladoText), yPos, { url: 'https://arteclaw.com' });

  // Save PDF with timestamp
  const timestamp = now.toISOString().replace(/:/g, '-').slice(0, 19); // YYYY-MM-DDTHH-MM-SS
  const filename = `blockchain_address_${addressInfo.network.id}_${timestamp}.pdf`;
  doc.save(filename);
}
